/**
 * Password Strength Indicator Component
 * Shows real-time password strength and validation feedback
 */

import React from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { getPasswordStrength } from '../utils/validation';

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showRequirements = true,
}) => {
  const strength = getPasswordStrength(password);

  const getStrengthColor = () => {
    switch (strength) {
      case 'weak':
        return 'bg-red-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'strong':
        return 'bg-green-500';
    }
  };

  const getStrengthWidth = () => {
    switch (strength) {
      case 'weak':
        return 'w-1/3';
      case 'medium':
        return 'w-2/3';
      case 'strong':
        return 'w-full';
    }
  };

  const getStrengthTextColor = () => {
    switch (strength) {
      case 'weak':
        return 'text-red-400';
      case 'medium':
        return 'text-yellow-400';
      case 'strong':
        return 'text-green-400';
    }
  };

  const requirements = [
    { text: 'At least 12 characters', met: password.length >= 12 },
    { text: 'At least 2 uppercase letters', met: (password.match(/[A-Z]/g) || []).length >= 2 },
    { text: 'At least 2 lowercase letters', met: (password.match(/[a-z]/g) || []).length >= 2 },
    { text: 'At least 2 numbers', met: (password.match(/[0-9]/g) || []).length >= 2 },
    {
      text: 'At least 2 special characters',
      // eslint-disable-next-line no-useless-escape
      met: (password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || []).length >= 2,
    },
  ];

  if (!password) return null;

  return (
    <div className="mt-2">
      {/* Strength Bar */}
      <div className="flex items-center space-x-2 mb-2">
        <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
          <div className={`h-full transition-all duration-300 ${getStrengthColor()} ${getStrengthWidth()}`} />
        </div>
        <span className={`text-xs font-medium ${getStrengthTextColor()}`}>
          {strength === 'weak' ? 'Weak' : strength === 'medium' ? 'Medium' : 'Strong'}
        </span>
      </div>

      {/* Requirements List */}
      {showRequirements && (
        <div className="space-y-1 text-xs">
          {requirements.map((req, index) => (
            <div key={index} className="flex items-center space-x-2">
              {req.met ? (
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-white/30 flex-shrink-0" />
              )}
              <span className={req.met ? 'text-green-400' : 'text-white/50'}>{req.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;
